
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useState } from "react";
import { PricingPlanCard } from "./components/PricingPlanCard";
import { EnterprisePlan } from "./components/EnterprisePlan";

interface SlidingPricingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onGetStarted: () => void;
}

export const SlidingPricingPanel = ({ isOpen, onClose, onGetStarted }: SlidingPricingPanelProps) => {
  const [isYearly, setIsYearly] = useState(true);
  const [expandedFeatures, setExpandedFeatures] = useState<string[]>([]);

  const toggleFeatureSection = (section: string) => {
    setExpandedFeatures(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const basicFeatures = [
    {
      title: "Basic Studio Tools",
      features: [
        "Essential Surgical Planning Tools",
        "Basic Templates & Scenarios",
        "Basic AI Analysis"
      ]
    },
    {
      title: "Studio Pro Tools",
      isLocked: true,
      features: []
    },
    {
      title: "thePlasticsPro Tools",
      isLocked: true,
      features: []
    }
  ];

  const proFeatures = [
    {
      title: "Basic Studio Tools",
      features: [
        "Essential Surgical Planning Tools",
        "Basic Templates & Scenarios",
        "Basic AI Analysis"
      ]
    },
    {
      title: "Studio Pro Tools",
      features: [
        "Advanced AI Analysis",
        "Advanced Templates and Scenarios",
        "Export and Share",
        "Unlimited Storage"
      ]
    },
    {
      title: "thePlasticsPro Tools",
      features: [
        "+ 200 Evidence-Based Articles",
        "+ 10,000 Verified Flashcards",
        "Innovative Anki Deck Builder",
        "Interview Prep so you Rank #1",
        "Concise Journal Club Reviews",
        "Interactive Clinical Cases",
        "thePlasticsPaper every Sunday",
        "and more!"
      ]
    }
  ];

  return (
    <div
      className={cn(
        "fixed inset-0 z-[99]",
        "bg-black/10 backdrop-blur-[0.5px]",
        "transition-all duration-500 ease-in-out",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
    >
      <div 
        className={cn(
          "absolute inset-0 pt-16",
          "overflow-y-auto",
          "transition-transform duration-500 ease-in-out",
          isOpen ? "translate-y-0" : "translate-y-full"
        )}
      >
        <div className="min-h-full p-6 pt-0 max-w-5xl mx-auto">
          <div className="absolute right-4 top-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="hover:bg-gray-100/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Choose Your Plan
            </h2>
            <p className="text-gray-600">
              Select the plan that best fits your needs
            </p>

            <div className="mt-6 flex items-center justify-center gap-3">
              <span className={cn(
                "text-sm transition-colors",
                !isYearly ? "text-gray-900 font-medium" : "text-gray-500"
              )}>Monthly</span>
              <Switch
                checked={isYearly}
                onCheckedChange={setIsYearly}
                className="data-[state=checked]:bg-[#6366F1]"
              />
              <span className={cn(
                "text-sm transition-colors",
                isYearly ? "text-gray-900 font-medium" : "text-gray-500"
              )}>Yearly</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <PricingPlanCard
              title="Studio Only"
              description="Essential tools for basic planning"
              price={isYearly ? "$110" : "$11"}
              period={isYearly ? "year" : "month"}
              savings={isYearly ? "Save $22" : undefined}
              sessionInfo="25 AI Analysis/month"
              features={basicFeatures}
              expandedFeatures={expandedFeatures}
              onFeatureToggle={toggleFeatureSection}
              onGetStarted={onGetStarted}
            />

            <PricingPlanCard
              title="thePlasticsPro"
              description="The Entire Studio + All thePlasticsPro Tools"
              price={isYearly ? "$169" : "$16.99"}
              period={isYearly ? "year" : "month"}
              savings={isYearly ? "Save $35" : undefined}
              sessionInfo="Unlimited AI Analysis"
              features={proFeatures}
              variant="pro"
              expandedFeatures={expandedFeatures}
              onFeatureToggle={toggleFeatureSection}
              onGetStarted={onGetStarted}
              popular
            />
          </div>

          <EnterprisePlan />
        </div>
      </div>
    </div>
  );
}
